import * as React from 'react'
import useEmblaCarousel, {
  type UseEmblaCarouselType
} from 'embla-carousel-react'

import { cn } from '~/lib/utils'
import { Button } from '~/components/UI/shadcn/button'
import ArrowLeft from '../../experimental/CarouselTest/CarouselControls/icon/arrow-left'
import ArrowRight from '../../experimental/CarouselTest/CarouselControls/icon/arrow-right'

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

type CarouselProps = {
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  orientation?: 'horizontal' | 'vertical'
  setApi?: (api: CarouselApi) => void
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  scrollPrev: () => void
  scrollNext: () => void
  canScrollPrev: boolean
  canScrollNext: boolean
} & CarouselProps

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

function useCarousel() {
  const context = React.useContext(CarouselContext)

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />')
  }

  return context
}

const Carousel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & CarouselProps
>(
  (
    {
      orientation = 'horizontal',
      opts,
      setApi,
      plugins,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === 'horizontal' ? 'x' : 'y'
      },
      plugins
    )
    const [canScrollPrev, setCanScrollPrev] = React.useState(false)
    const [canScrollNext, setCanScrollNext] = React.useState(false)

    const onSelect = React.useCallback((api: CarouselApi) => {
      if (!api) {
        return
      }

      setCanScrollPrev(api.canScrollPrev())
      setCanScrollNext(api.canScrollNext())
    }, [])

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev()
    }, [api])

    const scrollNext = React.useCallback(() => {
      api?.scrollNext()
    }, [api])

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault()
          scrollPrev()
        } else if (event.key === 'ArrowRight') {
          event.preventDefault()
          scrollNext()
        }
      },
      [scrollPrev, scrollNext]
    )

    React.useEffect(() => {
      if (!api || !setApi) {
        return
      }

      setApi(api)
    }, [api, setApi])

    React.useEffect(() => {
      if (!api) {
        return
      }

      onSelect(api)
      api.on('reInit', onSelect)
      api.on('select', onSelect)

      return () => {
        api?.off('select', onSelect)
      }
    }, [api, onSelect])

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation:
            orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn('relative', className)}
          role='region'
          aria-roledescription='carousel'
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    )
  }
)
Carousel.displayName = 'Carousel'

const CarouselContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { carouselRef, orientation } = useCarousel()

  return (
    <div ref={carouselRef} className='overflow-hidden'>
      <div
        ref={ref}
        className={cn(
          'flex',
          orientation === 'horizontal' ? '-ml-xs' : '-mt-xs flex-col',
          className
        )}
        {...props}
      />
    </div>
  )
})
CarouselContent.displayName = 'CarouselContent'

const CarouselItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { orientation } = useCarousel()

  return (
    <div
      ref={ref}
      role='group'
      aria-roledescription='slide'
      className={cn(
        'min-w-0 shrink-0 grow-0 basis-full',
        orientation === 'horizontal' ? 'pl-xs' : 'pt-xs',
        className
      )}
      {...props}
    />
  )
})
CarouselItem.displayName = 'CarouselItem'

const CarouselPrevious = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button> & {
    className?: string
    variant?: string
    size?: string
  }
>(({ className, variant = 'outline', size = 'icon', ...props }, ref) => {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel()

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      className={cn(
        'group absolute h-[36px] w-[36px] overflow-hidden rounded-full',
        orientation === 'horizontal'
          ? 'top-1/2 -translate-y-1/2'
          : 'left-1/2 -translate-x-1/2 rotate-90',
        className
      )}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <ArrowLeft />
      <span className='sr-only'>Previous slide</span>

      <svg
        className='absolute z-[-1] h-full w-full origin-bottom scale-y-0 opacity-20 transition-all duration-200 group-hover:scale-y-100 group-focus-visible:scale-y-100'
        viewBox='0 0 100 100'
        xmlns='http://www.w3.org/2000/svg'
      >
        <rect width='100' height='100' />
      </svg>
    </Button>
  )
})
CarouselPrevious.displayName = 'CarouselPrevious'

const CarouselNext = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button> & {
    className?: string
    variant?: string
    size?: string
  }
>(({ className, variant = 'outline', size = 'icon', ...props }, ref) => {
  const { orientation, scrollNext, canScrollNext } = useCarousel()

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      className={cn(
        'group absolute h-[36px] w-[36px] overflow-hidden rounded-full',
        orientation === 'horizontal'
          ? 'top-1/2 -translate-y-1/2'
          : 'left-1/2 -translate-x-1/2 rotate-90',
        className
      )}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <ArrowRight />
      <span className='sr-only'>Next slide</span>

      <svg
        className='absolute z-[-1] h-full w-full origin-bottom scale-y-0 opacity-20 transition-all duration-200 group-hover:scale-y-100 group-focus-visible:scale-y-100'
        viewBox='0 0 100 100'
        xmlns='http://www.w3.org/2000/svg'
      >
        <rect width='100' height='100' />
      </svg>
    </Button>
  )
})
CarouselNext.displayName = 'CarouselNext'

const CarouselDots = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, ref) => {
  const { api, orientation } = useCarousel()
  // const [updateState, setUpdateState] = React.useState(false) // Original state - updated below to remove error
  const [, setUpdateState] = React.useState(false)
  const toggleUpdateState = React.useCallback(
    () => setUpdateState(prevState => !prevState),
    []
  )

  React.useEffect(() => {
    if (api) {
      api.on('select', toggleUpdateState)
      api.on('reInit', toggleUpdateState)

      return () => {
        api.off('select', toggleUpdateState)
        api.off('reInit', toggleUpdateState)
      }
    }
  }, [api, toggleUpdateState])

  const numberOfSlides = api?.scrollSnapList().length || 0
  const currentSlide = api?.selectedScrollSnap() || 0

  if (numberOfSlides > 1) {
    return (
      <div ref={ref} className={`flex justify-center ${props.className}`}>
        {Array.from({ length: numberOfSlides }, (_, i) => (
          <div
            key={i}
            className={cn(
              `h-2xs-plus w-2xs-plus relative flex overflow-hidden transition-all duration-200 ${
                i === currentSlide
                  ? orientation === 'horizontal'
                    ? 'w-m'
                    : 'h-[28px]'
                  : ''
              }`
            )}
          >
            <Button
              className={`h-full rounded-none p-0 ${
                i === currentSlide
                  ? api?.plugins()?.autoplay
                    ? 'opacity-60'
                    : ''
                  : 'cursor-pointer opacity-60 hover:opacity-100'
              }`}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => api?.scrollTo(i)}
            />
            {i === currentSlide && api?.plugins()?.autoplay && (
              <svg
                id='svg-animation'
                className='absolute top-0 h-full w-full transition-all duration-200'
                xmlns='http://www.w3.org/2000/svg'
              >
                <rect width='100' height='100'>
                  <set attributeName='width' to='0' />
                  <animate
                    attributeName='width'
                    begin='0s'
                    dur='14s'
                    to='100'
                    restart='always'
                  />
                </rect>
              </svg>
            )}
          </div>
        ))}
      </div>
    )
  } else {
    return <></>
  }
})
CarouselDots.displayName = 'CarouselDots'

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  CarouselDots
}
